import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { SortTh, sortRows, nextSort, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE as ROW_LINE } from './tableSort';
import { ActionsCell } from './ActionsMenu';

// The column of a row's actions: shown as one "Actions" list (ActionsMenu).
const isActionColumn = (column) => column.accessor === '_actions' || /^actions?$/i.test(String(column.header || '').trim());

/**
 * Table - A reusable minimalist table component
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {boolean} props.isLoading - Whether the table is loading
 * @param {string} props.emptyMessage - Message to display when there's no data
 * @param {boolean} props.compact - Whether to use compact styling
 * @param {boolean} props.hoverable - Whether rows should change on hover
 * @param {string} props.className - Additional classes to apply
 * @param {Function} props.onRowClick - Optional callback when a row is clicked
 */
const Table = ({
  columns = [],
  data = [],
  isLoading = false,
  emptyMessage = "No data available",
  compact = false,
  hoverable = true,
  className = '',
  headerClassName = '',
  rowClassName,
  onRowClick,
  sort: controlledSort,
  onSortChange,
}) => {
  const cellPadding = compact ? 'px-3 py-2' : 'px-4 py-3';
  const headerPadding = compact ? 'px-3 py-2.5' : 'px-4 py-3';

  // Clicking a column header sorts by it. A page that splits its list into
  // pages passes `sort` and `onSortChange` and sorts the whole list itself
  // (useTableSort); otherwise the table sorts the rows it was given.
  const controlled = typeof onSortChange === 'function';
  const [ownSort, setOwnSort] = useState(null);
  const sort = controlled ? controlledSort : ownSort;
  const setSort = controlled ? onSortChange : setOwnSort;
  const rows = useMemo(() => (controlled ? data : sortRows(data, ownSort)), [controlled, data, ownSort]);
  const isSortable = (column) => column.sortable !== false
    && !isActionColumn(column)
    && Boolean(String(column.header || '').trim());
  const valueOf = (column) => (typeof column.sortValue === 'function'
    ? column.sortValue
    : (row) => row?.[column.accessor]);
  const keyOf = (column, index) => `${column.accessor || 'col'}:${index}`;

  return (
    // No frame on the left and right: the table runs the full width of its
    // area, and a line under every row (the last included) divides them.
    <div className={`w-full overflow-x-auto ${className}`}>
      <table className="w-full min-w-full border-collapse">
        <thead>
          <tr className={`${TABLE_HEAD_ROW} ${headerClassName || ''}`.trim()}>
            {columns.map((column, index) => (
              <SortTh
                key={index}
                label={column.header}
                sortKey={isSortable(column) ? keyOf(column, index) : null}
                value={valueOf(column)}
                sort={sort}
                onSort={(key, value) => setSort(nextSort(sort, key, value))}
                className={`${headerPadding} ${TABLE_HEAD_TEXT} whitespace-nowrap`}
                style={{ width: column.width }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr className={ROW_LINE}>
              <td colSpan={columns.length} className="text-center py-8 bg-white dark:bg-slate-900">
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600"></div>
                  <span className="text-sm text-gray-500 dark:text-slate-400">Loading...</span>
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr className={ROW_LINE}>
              <td colSpan={columns.length} className="text-center py-8 text-sm text-gray-400 dark:text-slate-500 bg-white dark:bg-slate-900">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => {
              const extraRowClass = typeof rowClassName === 'function'
                ? rowClassName(row, rowIndex)
                : (rowClassName || '');
              // Always apply dark mode alternating bg; light mode bg only if no custom rowClassName
              const lightBg = extraRowClass ? '' : (rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50');
              const darkBg = rowIndex % 2 === 0 ? 'dark:bg-slate-800' : 'dark:bg-slate-900';
              return (
                <tr
                  key={rowIndex}
                  className={`${ROW_LINE} ${lightBg} ${darkBg} ${extraRowClass} ${hoverable ? 'hover:bg-blue-50 dark:hover:bg-slate-700' : ''} ${onRowClick ? 'cursor-pointer' : ''} transition-colors duration-100`.trim()}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column, colIndex) => (
                    <td key={colIndex} className={`${cellPadding} text-sm text-gray-900 dark:text-slate-100`}>
                      {isActionColumn(column)
                        ? <ActionsCell>{column.cell ? column.cell(row) : row[column.accessor]}</ActionsCell>
                        : (column.cell ? column.cell(row) : row[column.accessor])}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

/**
 * ResponsiveTable - The app's list table: a table at every screen size,
 * scrolling sideways when its columns are wider than the screen.
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {boolean} props.isLoading - Whether the table is loading
 * @param {string} props.emptyMessage - Message to display when there's no data
 * @param {string} props.className - Additional classes to apply
 * @param {Function} props.onRowClick - Optional callback when a row is clicked
 */
export const ResponsiveTable = ({
  columns = [],
  data = [],
  isLoading = false,
  emptyMessage = "No data available",
  className = '',
  onRowClick,
  rowClassName,
  headerClassName = '',
  sort,
  onSortChange,
  compact = false,
}) => (
  <div className={className}>
    <Table
      compact={compact}
      columns={columns}
      data={data}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
      headerClassName={headerClassName}
      rowClassName={rowClassName}
      sort={sort}
      onSortChange={onSortChange}
    />
  </div>
);

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      cell: PropTypes.func,
      width: PropTypes.string,
      sortable: PropTypes.bool,
      sortValue: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  compact: PropTypes.bool,
  hoverable: PropTypes.bool,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onRowClick: PropTypes.func,
  sort: PropTypes.object,
  onSortChange: PropTypes.func,
};

ResponsiveTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      cell: PropTypes.func,
      width: PropTypes.string,
      sortable: PropTypes.bool,
      sortValue: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  headerClassName: PropTypes.string,
  sort: PropTypes.object,
  onSortChange: PropTypes.func,
};

export default Table;
